<template>
  <v-container fluid class="pa-0">
    <!-- Header -->
    <div class="mb-7">
      <div style="font-size: 30px; font-weight: 800">Skills Assessment</div>
      <div class="text-subtitle-2 mt-1">
        <span class="text-grey"
          >Complete all assessments to update your skills profile for internship
          matching</span
        >
      </div>
    </div>

    <!-- Loading -->
    <div v-if="initialLoading" class="d-flex justify-center py-12">
      <v-progress-circular indeterminate color="#10b981" size="48" />
    </div>

    <template v-else>
      <!-- Assessments to take -->
      <template v-if="table_data.length > 0">
        <div
          class=""
          :class="
            $vuetify.display.mdAndUp ? 'd-flex justify-space-between' : ''
          "
        >
          <div class="d-flex align-center gap-2 mb-4">
            <v-icon color="#10b981" size="20"
              >mdi-clipboard-list-outline</v-icon
            >
            <span class="text-subtitle-2 font-weight-bold"
              >Assessments to Take</span
            >
            <v-chip size="x-small" color="#10b981" variant="tonal">{{
              table_data.length
            }}</v-chip>
          </div>
          <div class="d-flex align-center gap-2 mb-4">
            <v-icon color="#10b981" size="20">mdi-calendar</v-icon>
            <span class="text-subtitle-2 font-weight-bold"
              >Available Until</span
            >
            <v-chip size="x-small" color="#10b981" variant="tonal">{{
              availability
            }}</v-chip>
          </div>
        </div>
        <v-row>
          <v-col
            cols="12"
            sm="6"
            md="4"
            v-for="item in table_data"
            :key="item.id"
          >
            <v-card
              variant="outlined"
              rounded="xl"
              class="assessment-card pa-5 h-100 d-flex flex-column"
            >
              <div class="d-flex align-start gap-3 mb-4">
                <div
                  class="assess-icon-wrap d-flex align-center justify-center rounded-circle flex-shrink-0"
                >
                  <v-icon color="#10b981" size="22"
                    >mdi-clipboard-check-outline</v-icon
                  >
                </div>
                <div class="flex-grow-1">
                  <div class="text-body-1 font-weight-bold">
                    {{ item.title }}
                  </div>
                  <div
                    class="text-caption text-grey mt-1 d-flex align-center gap-1"
                  >
                    <v-icon size="13">mdi-clock-outline</v-icon>
                    {{
                      item.time_limit
                        ? `${item.time_limit} min`
                        : "No time limit"
                    }}
                  </div>
                </div>
              </div>
              <v-spacer />
              <v-btn
                color="#10b981"
                variant="flat"
                class="text-white"
                prepend-icon="mdi-play-circle-outline"
                block
                :disabled="shouldDisableAssessment"
                rounded="lg"
                @click="viewItem(item)"
              >
                Start Assessment
              </v-btn>
            </v-card>
          </v-col>
        </v-row>
      </template>

      <!-- Completed state -->
      <template v-else>
        <v-card
          variant="outlined"
          rounded="xl"
          class="completed-card pa-8 text-center"
        >
          <div class="seal-wrap mx-auto mb-4">
            <v-icon size="40" color="#10b981">mdi-seal</v-icon>
          </div>
          <div class="text-h5 font-weight-bold mb-1">Assessment Completed</div>
          <div class="text-body-2 text-grey-darken-1 mb-6">
            Your skills profile has been updated for job matching.
          </div>

          <!-- Stats row -->
          <v-row dense class="mb-6">
            <v-col cols="4">
              <v-card variant="tonal" color="success" rounded="lg" class="pa-3">
                <div class="text-h5 font-weight-bold" style="color: #10b981">
                  {{ overall ?? "—" }}
                </div>
                <div class="text-caption text-grey-darken-1 font-weight-medium">
                  Overall Score
                </div>
              </v-card>
            </v-col>
            <v-col cols="4">
              <v-card variant="outlined" rounded="lg" class="pa-3">
                <div class="text-h5 font-weight-bold">
                  {{ correct_ans ?? "—" }}
                </div>
                <div class="text-caption text-grey-darken-1 font-weight-medium">
                  Correct Answers
                </div>
              </v-card>
            </v-col>
            <v-col cols="4">
              <v-card variant="outlined" rounded="lg" class="pa-3">
                <div class="text-h5 font-weight-bold">
                  {{ skill_assessment_cnt ?? "—" }}
                </div>
                <div class="text-caption text-grey-darken-1 font-weight-medium">
                  Assessments Taken
                </div>
              </v-card>
            </v-col>
          </v-row>

          <!-- Recommended track -->
          <v-card
            v-if="getRecommendedResult.name"
            rounded="lg"
            class="pa-4 text-left"
            style="border: 1.5px solid #10b981; background: #f0fdf4"
          >
            <div class="d-flex align-center gap-2 mb-3">
              <v-icon color="#10b981">mdi-star-circle-outline</v-icon>
              <span
                class="text-subtitle-2 font-weight-bold"
                style="color: #10b981"
              >
                Best Match — Recommended Internship Track
              </span>
            </div>
            <div
              class="d-flex align-center justify-space-between flex-wrap gap-3"
            >
              <div>
                <div class="text-body-1 font-weight-bold">
                  {{ getRecommendedResult.name }}
                </div>
                <div class="text-caption text-grey-darken-1 mt-1">
                  {{ getRecommendedResult.description }}
                </div>
              </div>
              <div class="text-center">
                <div class="text-h4 font-weight-bold" style="color: #10b981">
                  {{ getRecommendedResult.matchPercentage }}%
                </div>
                <div class="text-caption text-grey">Match</div>
              </div>
            </div>
          </v-card>
        </v-card>
      </template>
    </template>

    <!-- Dialogs -->
    <StudentSkillAssessmentDialog :data="itemData" :action="action" />

    <v-dialog v-model="dialogConfirmDelete" max-width="500">
      <v-card>
        <v-card-title class="text-h6">Confirm Deletion</v-card-title>
        <v-card-text class="text-body-1"
          >Are you sure you want to delete this item?</v-card-text
        >
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="dialogConfirmDelete = false"
            >Cancel</v-btn
          >
          <v-btn color="error" @click="confirmDelete">Confirm</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <fade-away-message-component
      displayType="default"
      v-model="fadeAwayMessage.show"
      :message="fadeAwayMessage.message"
      :header="fadeAwayMessage.header"
      :type="fadeAwayMessage.type"
    />
  </v-container>
</template>

<script>
import eventBus from "@/eventBus";
import StudentSkillAssessmentDialog from "../Dialogs/Forms/SkillAssessment/StudentSkillAssessmentDialog.vue";

export default {
  components: { StudentSkillAssessmentDialog },

  data: () => ({
    initialLoading: false,
    loading: false,
    table_data: [],
    results: [],
    overall: null,
    correct_ans: null,
    skill_assessment_cnt: null,
    itemData: null,
    action: null,
    deleteData: null,
    dialogConfirmDelete: false,
    fadeAwayMessage: {
      show: false,
      type: "success",
      header: "Successfully Deleted!",
      message: "",
      top: 10,
    },
    availability: null,
    sg: null,
  }),

  computed: {
    getRecommendedResult() {
      if (this.results && this.results.length > 0) {
        return this.results[0];
      }
      return { name: null, description: null, matchPercentage: null };
    },

    shouldDisableAssessment() {
      const now = new Date();

      if (this.sg) {
        const datePart = this.sg.date_until;
        const timePart = this.sg.time_until;

        const concatenatedString = `${datePart}T${timePart}`;
        const secondDateTime = new Date(concatenatedString);
        console.log();
        return secondDateTime.getTime() < now.getTime();
      }
      return true;
    },
  },

  mounted() {
    this.initialize();
    eventBus.on("closeStudentSkillAssessmentDialog", () => {
      this.initialize();
    });
  },

  beforeUnmount() {
    eventBus.off("closeStudentSkillAssessmentDialog");
  },

  methods: {
    initialize() {
      this.initialLoading = true;
      this.loading = true;
      this.axiosCall("/skill-assessement/student/getToAssess/1", "GET")
        .then((res) => {
          if (res) {
            console.log("DATA", res.data);
            this.table_data = res.data.data;
            this.sg = res.data.sg;
            if (this.sg) {
              this.availability =
                this.formatDate(res.data.sg.date_until) +
                " " +
                this.formatTime(res.data.sg.time_until);
            }
            if (res.data.data.length === 0) {
              this.loadResult();
            }
          }
        })
        .finally(() => {
          this.initialLoading = false;
          this.loading = false;
        });
    },

    loadResult() {
      this.axiosCall("/skill-assessement/student/getToAssess/2", "GET").then(
        (res) => {
          if (res) {
            this.results = res.data.data;
            this.overall = res.data.overall_score;
            this.correct_ans = res.data.correct_answers;
            this.skill_assessment_cnt = res.data.skill_assessment_count;
          }
        },
      );
    },

    viewItem(item) {
      this.itemData = item;
      this.action = "View";
    },

    confirmDelete() {
      this.axiosCall("/skill-assessement/" + this.deleteData.id, "DELETE").then(
        () => {
          this.fadeAwayMessage.show = true;
          this.itemData = null;
          this.initialize();
        },
      );
    },
  },
};
</script>

<style scoped>
.assessment-card {
  transition: box-shadow 0.2s;
}
.assessment-card:hover {
  box-shadow: 0 4px 16px rgba(16, 185, 129, 0.12) !important;
}

.assess-icon-wrap {
  width: 44px;
  height: 44px;
  min-width: 44px;
  background: #ecfdf5;
}

.seal-wrap {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: #ecfdf5;
  display: flex;
  align-items: center;
  justify-content: center;
}

.gap-1 {
  gap: 4px;
}
.gap-2 {
  gap: 8px;
}
.gap-3 {
  gap: 12px;
}
</style>
